import {
  createContext,
  useContext,
  useRef,
  type ReactNode,
  type RefObject,
} from "react"

/**
 * Provides a ref to the main scroll container (&lt;main&gt; in AppLayout).
 *
 * Why this exists:
 * AppLayout wraps children in `<main className="overflow-y-auto">` —
 * content scrolls inside that element, NOT the window. Components that
 * need the scroll element (e.g. @tanstack/react-virtual `useVirtualizer`,
 * pull-to-refresh, infinite scroll) use this context instead of assuming
 * window scroll.
 */

interface MainScrollContextValue {
  /** Ref to the <main> scroll container element */
  scrollRef: RefObject<HTMLElement | null>
}

const MainScrollContext = createContext<MainScrollContextValue | null>(null)

export function MainScrollProvider({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLElement | null>(null)

  return (
    <MainScrollContext.Provider value={{ scrollRef }}>
      {children}
    </MainScrollContext.Provider>
  )
}

/**
 * Returns the ref to the main scroll container.
 * Use `scrollRef.current` as `getScrollElement` for @tanstack/react-virtual.
 */
export function useMainScroll() {
  const ctx = useContext(MainScrollContext)
  if (!ctx)
    throw new Error("useMainScroll must be used within <MainScrollProvider>")
  return ctx
}
